import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import NotificationTemplateService from "../../services/NotificationTemplateService";

const SENDGRID_BASE = "https://mc.sendgrid.com/dynamic-templates/";

const EMPTY_FORM = {
  idProcess: "",
  typeTemplate: "EMAIL",
  idTemplate: "",
  title: "",
  template: "",
  idEmployer: "",
  status: "ACTIVE",
  lang: "",
  notificationTypeId: "",
  bccTo: "",
  images: "",
  displayMode: "PUSH",
};

// ── {{TOKEN}} highlighted preview ────────────────────────────────────────────
function TokenPreview({ text }) {
  if (!text) return null;
  const parts = text.split(/({{[^}]+}})/g);
  return (
    <Box
      component="div"
      sx={{
        fontFamily: "monospace",
        fontSize: "0.8rem",
        p: 1,
        bgcolor: "grey.50",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "grey.200",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
        lineHeight: 1.8,
      }}
    >
      {parts.map((part, i) =>
        /^{{[^}]+}}$/.test(part) ? (
          <Box
            key={i}
            component="span"
            sx={{
              bgcolor: "warning.light",
              color: "warning.contrastText",
              borderRadius: "3px",
              px: 0.5,
              fontWeight: 600,
            }}
          >
            {part}
          </Box>
        ) : (
          <span key={i}>{part}</span>
        )
      )}
    </Box>
  );
}

// ── BCC chip input ────────────────────────────────────────────────────────────
function BccChipInput({ value, onChange }) {
  const [inputVal, setInputVal] = useState("");
  const chips = value
    ? value
        .split(",")
        .map((e) => e.trim())
        .filter(Boolean)
    : [];

  const addChip = () => {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    const next = [...chips, trimmed].join(",");
    onChange(next);
    setInputVal("");
  };

  const removeChip = (idx) => {
    const next = chips.filter((_, i) => i !== idx).join(",");
    onChange(next);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addChip();
    }
  };

  return (
    <Box>
      <Box display="flex" flexWrap="wrap" gap={0.5} mb={chips.length ? 1 : 0}>
        {chips.map((chip, i) => (
          <Chip
            key={i}
            label={chip}
            size="small"
            onDelete={() => removeChip(i)}
          />
        ))}
      </Box>
      <TextField
        size="small"
        label="Add BCC email (Enter or comma to add)"
        value={inputVal}
        onChange={(e) => setInputVal(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={addChip}
        fullWidth
        helperText="Press Enter or comma to confirm each address"
      />
    </Box>
  );
}

// ── Images multi-row input ────────────────────────────────────────────────────
function ImagesInput({ value, onChange }) {
  // Split on literal commas (URLs must encode commas as %2C)
  const urls = value ? value.split(",").map((u) => u.trim()) : [""];

  const updateUrl = (idx, newUrl) => {
    const next = [...urls];
    next[idx] = newUrl;
    onChange(next.join(","));
  };

  const addRow = () => {
    onChange([...urls, ""].join(","));
  };

  const removeRow = (idx) => {
    const next = urls.filter((_, i) => i !== idx);
    onChange(next.join(","));
  };

  return (
    <Stack spacing={1}>
      {urls.map((url, i) => (
        <Box key={i}>
          <Box display="flex" gap={1} alignItems="center">
            <TextField
              size="small"
              label={`Image URL ${i + 1}`}
              value={url}
              onChange={(e) => updateUrl(i, e.target.value)}
              fullWidth
              placeholder="https://example.com/image.png"
            />
            <Tooltip title="Remove">
              <IconButton size="small" onClick={() => removeRow(i)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addRow}
        sx={{ alignSelf: "flex-start" }}
        variant="outlined"
      >
        Add Image URL
      </Button>
    </Stack>
  );
}

// ── Main editor ───────────────────────────────────────────────────────────────
export default function NotificationTemplateEditor() {
  const { id } = useParams();
  const isNew = id === "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [form, setForm] = useState(EMPTY_FORM);
  const [feedback, setFeedback] = useState({
    open: false,
    type: "success",
    message: "",
  });

  const showFeedback = (type, message) =>
    setFeedback({ open: true, type, message });

  // ── Load notification types ─────────────────────────────────────────
  const { data: notificationTypes = [] } = useQuery(
    ["notification-types"],
    () => NotificationTemplateService.getNotificationTypes(),
    { select: (r) => r.data }
  );

  // ── Load existing record ──────────────────────────────────────────────
  const { isLoading, data: loadedData } = useQuery(
    ["notification-template", id],
    () => NotificationTemplateService.getById(id),
    {
      enabled: !isNew,
      select: (r) => r.data,
    }
  );

  useEffect(() => {
    if (!loadedData) return;
    setForm({
      idProcess: loadedData.idProcess || "",
      typeTemplate: loadedData.typeTemplate || "EMAIL",
      idTemplate: loadedData.idTemplate || "",
      title: loadedData.title || "",
      template: loadedData.template || "",
      idEmployer: loadedData.idEmployer || "",
      status: loadedData.status || "ACTIVE",
      lang: loadedData.lang || "",
      notificationTypeId: loadedData.notificationTypeId || "",
      bccTo: loadedData.bccTo || "",
      images: loadedData.images || "",
      displayMode: loadedData.displayMode || "PUSH",
    });
  }, [loadedData]);

  const createMutation = useMutation(
    (body) => NotificationTemplateService.create(body),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(["notification-templates"]);
        showFeedback("success", "Template created");
        navigate(
          `/communication/notification-templates/${res.data.idNotificationTemplate}`
        );
      },
      onError: () => showFeedback("error", "Failed to create template"),
    }
  );

  const updateMutation = useMutation(
    (body) => NotificationTemplateService.update(id, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["notification-templates"]);
        queryClient.invalidateQueries(["notification-template", id]);
        showFeedback("success", "Template saved");
      },
      onError: () => showFeedback("error", "Failed to save template"),
    }
  );

  const isSaving = createMutation.isLoading || updateMutation.isLoading;

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSave = () => {
    if (!form.idProcess.trim()) {
      showFeedback("error", "Process ID is required");
      return;
    }
    const body = {
      idProcess: form.idProcess.trim(),
      typeTemplate: form.typeTemplate,
      idTemplate: form.idTemplate.trim() || null,
      title: form.title.trim() || null,
      template: form.template.trim() || null,
      idEmployer: form.idEmployer.trim() || null,
      status: form.status,
      lang: form.lang.trim() || null,
      notificationTypeId: form.notificationTypeId.trim() || null,
      bccTo: form.bccTo.trim() || null,
      images: form.images.trim() || null,
      displayMode: form.displayMode || null,
    };
    if (isNew) {
      createMutation.mutate(body);
    } else {
      updateMutation.mutate(body);
    }
  };

  const isEmail = form.typeTemplate === "EMAIL";
  const isVoice = form.typeTemplate === "VOICE";
  const isApp = form.typeTemplate === "APP";

  if (!isNew && isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box maxWidth={780}>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <IconButton
          size="small"
          onClick={() => navigate("/communication/notification-templates")}
        >
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6">
          {isNew ? "New Notification Template" : `Edit Template #${id}`}
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Stack spacing={2.5}>
          {/* Row 1: Process + Type */}
          <Box display="flex" gap={2}>
            <TextField
              label="Process ID"
              value={form.idProcess}
              onChange={set("idProcess")}
              required
              fullWidth
              size="small"
              helperText="e.g. Bill Pay Batch Debit"
            />
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={form.typeTemplate}
                label="Type"
                onChange={set("typeTemplate")}
              >
                <MenuItem value="EMAIL">EMAIL</MenuItem>
                <MenuItem value="SMS">SMS</MenuItem>
                <MenuItem value="VOICE">VOICE</MenuItem>
                <MenuItem value="APP">APP</MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Row 2: Status + Lang + Employer */}
          <Box display="flex" gap={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={form.status === "ACTIVE"}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      status: e.target.checked ? "ACTIVE" : "INACTIVE",
                    }))
                  }
                  color="success"
                />
              }
            />
            <TextField
              label="Lang"
              value={form.lang}
              onChange={set("lang")}
              size="small"
              inputProps={{ maxLength: 2 }}
              sx={{ width: 180 }}
              placeholder="en"
            />
            <TextField
              label="Employer ID"
              value={form.idEmployer}
              onChange={set("idEmployer")}
              size="small"
              fullWidth
              helperText="Leave blank for all employers"
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Notification Type</InputLabel>
              <Select
                label="Notification Type"
                value={form.notificationTypeId}
                onChange={set("notificationTypeId")}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {notificationTypes.map((nt) => (
                  <MenuItem
                    key={nt.notificationTypeId}
                    value={nt.notificationTypeId}
                  >
                    {nt.notificationTypeId}
                    {nt.description ? ` — ${nt.description}` : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* EMAIL branch */}
          {isEmail && (
            <>
              <Typography variant="subtitle2" color="text.secondary">
                SendGrid Template
              </Typography>
              <Box display="flex" gap={1} alignItems="flex-start">
                <Box flex={1}>
                  <TextField
                    label="SendGrid Template ID"
                    value={form.idTemplate}
                    onChange={set("idTemplate")}
                    size="small"
                    fullWidth
                    placeholder="d-xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  />
                  {!form.idTemplate && (
                    <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                      <Typography variant="caption" color="text.secondary">
                        No template yet?
                      </Typography>
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon fontSize="inherit" />}
                        href="https://mc.sendgrid.com/dynamic-templates"
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: "0.7rem",
                          py: 0.25,
                          px: 1,
                          minHeight: 0,
                        }}
                      >
                        Create in SendGrid
                      </Button>
                    </Box>
                  )}
                </Box>
                {form.idTemplate && (
                  <Box display="flex" gap={0.5} sx={{ mt: 0.25 }}>
                    <Tooltip title="Copy template ID to clipboard">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ContentCopyIcon fontSize="small" />}
                        onClick={() =>
                          navigator.clipboard.writeText(form.idTemplate)
                        }
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        Copy ID
                      </Button>
                    </Tooltip>
                    <Tooltip
                      title={
                        loadedData?.sendgridUrl
                          ? "Open the active version in the SendGrid editor"
                          : "Open this template in SendGrid"
                      }
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewIcon />}
                        href={
                          loadedData?.sendgridUrl ||
                          SENDGRID_BASE + form.idTemplate
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ whiteSpace: "nowrap" }}
                      >
                        SendGrid
                      </Button>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              <Typography variant="subtitle2" color="text.secondary" mt={1}>
                BCC Recipients
              </Typography>
              <BccChipInput
                value={form.bccTo}
                onChange={(val) => setForm((prev) => ({ ...prev, bccTo: val }))}
              />
            </>
          )}

          {/* SMS / VOICE / APP branch */}
          {!isEmail && (
            <>
              {/* APP: title + display mode */}
              {isApp && (
                <Box display="flex" gap={2}>
                  <TextField
                    label="Notification Title"
                    value={form.title}
                    onChange={set("title")}
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 255 }}
                    placeholder="e.g. New message from Rellevate"
                    helperText="Supports {{token}} placeholders"
                  />
                  <FormControl size="small" sx={{ minWidth: 180 }}>
                    <InputLabel>Display Mode</InputLabel>
                    <Select
                      label="Display Mode"
                      value={form.displayMode}
                      onChange={set("displayMode")}
                    >
                      <MenuItem value="PUSH">Push Notification</MenuItem>
                      <MenuItem value="IN_APP">In-App Overlay</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              )}
              <Box>
                {isVoice ? (
                  <>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      mb={1}
                    >
                      Audio URL (MP3)
                    </Typography>
                    <TextField
                      label="MP3 Audio URL"
                      value={form.images}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, images: e.target.value }))
                      }
                      size="small"
                      fullWidth
                      placeholder="https://your-cdn.com/recordings/welcome.mp3"
                      helperText="Optional pre-recorded MP3. If blank, the message text below will be read aloud via text-to-speech."
                    />
                  </>
                ) : (
                  <>
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      mb={1}
                    >
                      Media Images
                      <Typography component="span" variant="caption" ml={1}>
                        (comma-separated URLs — commas within URLs must be
                        encoded as %2C)
                      </Typography>
                    </Typography>
                    <ImagesInput
                      value={form.images}
                      onChange={(val) =>
                        setForm((prev) => ({ ...prev, images: val }))
                      }
                    />
                  </>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" color="text.secondary" mb={0.5}>
                  {isVoice ? "Message Body (TTS Fallback)" : "Message Body"}
                  {form.typeTemplate === "SMS" && (
                    <Typography
                      component="span"
                      variant="caption"
                      color={
                        form.template.length > 160 ? "error" : "text.secondary"
                      }
                      ml={1}
                    >
                      {form.template.length}/500 chars
                      {form.template.length > 160 && " (multi-part SMS)"}
                    </Typography>
                  )}
                </Typography>
                <TextField
                  value={form.template}
                  onChange={set("template")}
                  multiline
                  rows={6}
                  fullWidth
                  size="small"
                  placeholder="{{NAME}} your message here…"
                  inputProps={{ maxLength: 500 }}
                />
                {(form.template || (form.images && !isVoice)) && (
                  <Box mt={1}>
                    <Typography variant="caption" color="text.secondary">
                      Preview:
                    </Typography>
                    {form.images && !isVoice && (
                      <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                        {form.images
                          .split(",")
                          .map((u) => u.trim())
                          .filter(Boolean)
                          .map((url, i) => (
                            <Box
                              key={i}
                              component="img"
                              src={url}
                              alt={`preview-${i}`}
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                              sx={{
                                maxHeight: 104,
                                maxWidth: 260,
                                objectFit: "contain",
                                border: "1px solid",
                                borderColor: "grey.200",
                                borderRadius: 1,
                                display: "block",
                              }}
                            />
                          ))}
                      </Box>
                    )}
                    {form.template && <TokenPreview text={form.template} />}
                  </Box>
                )}
              </Box>
            </>
          )}
        </Stack>
      </Paper>

      <Box display="flex" gap={1} mt={2}>
        <Button variant="contained" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving…" : isNew ? "Create" : "Save"}
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate("/communication/notification-templates")}
          disabled={isSaving}
        >
          Cancel
        </Button>
      </Box>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={feedback.type}
          onClose={() => setFeedback((f) => ({ ...f, open: false }))}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
